import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Container } from '@mui/system'
import { Box, Button, Card, CircularProgress, Link, Stack, styled, Tab, Tabs, Typography } from '@mui/material'
import { Pause as PauseIcon } from '@mui/icons-material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChainProtocol, OperationType, READABLE_PROTOCOL } from '@archax/shared-types'
import { getPayoutDetailsById } from '../../../api/payouts'
import onApiError from '../../../util/on-api-error'
import { formatUTCMMDDYYYY } from '../../../util/formatters'
import { TabPanel } from '../../../components/ui/TabPanel'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs'
import Dialog from '../../../components/Dialog/Dialog'
import { createOperationForPayoutContract } from '../../../api/operations'
import tokenBalanceFormatter from '../../../util/token-balance-formatter'
import { renderPayoutStatus } from '../components/Status'
import SetPayoutPerIndex from '../components/SetPayoutPerIndex'
import SetEligibilityToken from '../components/SetEligibilityToken'
import SetPayoutToken from '../components/SetPayoutToken'
import { History } from './History'
import { Whitelist } from './Whitelist'

const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: '100px',
  h5: {
    color: '#fff',
    marginTop: '10px',
  },
}))

const StyledStackLinks = styled(Stack)(() => ({
  marginTop: '0.5rem',
  gap: '0.2rem',
  fontSize: '0.9em',
  label: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
  },
  'label, a': {
    alignSelf: 'flex-start',
  },
  a: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.2rem',
    fontSize: '0.9em',
  },
}))

const StyledStackDetails = styled(Stack)(() => ({
  marginTop: '1rem',
  padding: '1rem',
  backgroundColor: '#F8FBFD',
  borderRadius: '8px',
  gap: '1rem',
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
}))

const StyledStackActions = styled(Stack)(() => ({
  marginTop: '1rem',
  gap: '1rem',
}))

const WhiteButton = styled(Button)(() => ({
  color: '#000',
  backgroundColor: '#fff',
  '&:hover': {
    color: '#fff',
    backgroundColor: '#0070B8',
  },
}))

export default function ShowPayout() {
  const navigate = useNavigate()
  const location = useLocation()

  const { id } = useParams() as { id: string }
  const { t } = useTranslation()

  const [showPauseDialog, setShowPauseDialog] = useState(false)
  const [showUnpauseDialog, setShowUnpauseDialog] = useState(false)
  const [showSetPayoutPerIndexDialog, setShowSetPayoutPerIndexDialog] = useState(false)
  const [showSetEligibilityTokenDialog, setShowSetEligibilityTokenDialog] = useState(false)
  const [showSetPayoutTokenDialog, setShowSetPayoutTokenDialog] = useState(false)

  const { isLoading, data: payoutDetailsResponse } = useQuery(
    [`payout-contract-details-${id}`],
    () => getPayoutDetailsById(id),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )
  const { data: payoutDetails } = payoutDetailsResponse || {}

  const tabs = useMemo(
    () =>
      [
        {
          label: 'History',
          component: <History payoutContractId={id} payoutToken={payoutDetails?.payoutToken} />,
          visible: true,
          path: '/history',
        },
        {
          label: 'Whitelist',
          component: <Whitelist protocol={payoutDetails?.chain?.protocol as ChainProtocol} />,
          visible: true,
          path: '/whitelist',
        },
      ].filter((tab) => tab.visible),
    [payoutDetails],
  )
  let currentTabIndex = tabs.findIndex((tab) => location.pathname.endsWith(tab.path))
  if (currentTabIndex === -1) {
    currentTabIndex = 0
  }

  const { mutate: pauseMutation } = useMutation(
    () => createOperationForPayoutContract(OperationType.PayoutContractPause, {}, { payoutContractId: id }),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.pause.successMessage'))
      },
      onError: onApiError,
    },
  )

  const { mutate: unpauseMutation } = useMutation(
    () => createOperationForPayoutContract(OperationType.PayoutContractUnpause, {}, { payoutContractId: id }),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.unpause.successMessage'))
      },
      onError: onApiError,
    },
  )

  const handlePause = async () => {
    pauseMutation()
    setShowPauseDialog(false)
  }

  const handleUnpause = async () => {
    unpauseMutation()
    setShowUnpauseDialog(false)
  }

  const handleChangeTab = (event: React.SyntheticEvent, value: any) => {
    navigate(`/payouts/${id}${tabs[value].path}`)
  }

  return (
    <Container maxWidth="xl">
      <Breadcrumbs
        items={[
          { path: '/payouts', label: t('payoutContracts.showDetails.title') },
          { label: `${payoutDetails?.name || ''}` },
        ]}
      />
      {isLoading ? (
        <Wrapper>
          <CircularProgress />
          <Typography variant="h5">{t('payoutContracts.showDetails.loadingMessage')}</Typography>
        </Wrapper>
      ) : (
        <>
          <Card sx={{ p: 7, mb: 5 }}>
            <Stack display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Typography align="left" variant="h3">
                {payoutDetails?.name}
              </Typography>
              {renderPayoutStatus(payoutDetails.status)}
            </Stack>
            <StyledStackLinks display={'flex'} flexDirection={'column'}>
              <label>
                <span>{t('payoutContracts.labels.contractAddress')}:</span>
                <Link href={payoutDetails.explorerLinks.payoutContract.address.url} rel="noreferrer" target="_blank">
                  {payoutDetails.explorerLinks.payoutContract.address.value}
                </Link>
              </label>
              <label>
                <span>{t('payoutContracts.labels.eligibilityToken')}:</span>
                <Link href={payoutDetails.explorerLinks.eligibilityToken.address.url} rel="noreferrer" target="_blank">
                  {payoutDetails.explorerLinks.eligibilityToken.address.value}
                </Link>
              </label>
              <label>
                <span>{t('payoutContracts.labels.payoutToken')}:</span>
                <Link href={payoutDetails.explorerLinks.payoutToken.address.url} rel="noreferrer" target="_blank">
                  {payoutDetails.explorerLinks.payoutToken.address.value}
                </Link>
              </label>
            </StyledStackLinks>
            <StyledStackDetails display={'grid'} gridTemplateColumns={'1.5fr 1.5fr 1fr 1fr 1fr'}>
              <label>
                <span>{t('payoutContracts.labels.startDate')}:</span>
                <strong>{formatUTCMMDDYYYY(payoutDetails?.startAt)}</strong>
              </label>
              <label>
                <span>{t('payoutContracts.labels.endDate')}:</span>
                <strong>{formatUTCMMDDYYYY(payoutDetails?.endAt)}</strong>
              </label>
              <label>
                <span>{t('payoutContracts.labels.status')}:</span>
                <strong>{t(`payoutContracts.statuses.${payoutDetails?.status}` as any)}</strong>
              </label>
              <label>
                <span>{t('payoutContracts.labels.protocol')}:</span>
                <strong>{READABLE_PROTOCOL[payoutDetails?.chain.protocol as ChainProtocol]}</strong>
              </label>
              <label>
                <span>{t('payoutContracts.labels.payoutIndex')}:</span>
                <strong>
                  {tokenBalanceFormatter(payoutDetails?.payoutPerIndex, payoutDetails.payoutToken.decimals)}
                </strong>
              </label>
            </StyledStackDetails>
            <StyledStackActions display={'flex'} flexDirection={'row'}>
              <WhiteButton
                variant="contained"
                onClick={() =>
                  payoutDetails.onChainData.isPaused ? setShowUnpauseDialog(true) : setShowPauseDialog(true)
                }
              >
                <PauseIcon />
                {payoutDetails.onChainData.isPaused
                  ? t('payoutContracts.actions.unpause')
                  : t('payoutContracts.actions.pause')}
              </WhiteButton>
              <Button variant="contained" onClick={() => setShowSetPayoutPerIndexDialog(true)}>
                {t('payoutContracts.actions.updatePayoutIndex')}
              </Button>
              <Button variant="contained" onClick={() => setShowSetEligibilityTokenDialog(true)}>
                {t('payoutContracts.actions.updateEligibilityToken')}
              </Button>
              <Button variant="contained" onClick={() => setShowSetPayoutTokenDialog(true)}>
                {t('payoutContracts.actions.updatePayoutToken')}
              </Button>
            </StyledStackActions>
          </Card>
          <Card sx={{ p: 7, mb: 5 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTabIndex} onChange={handleChangeTab}>
                {tabs.map((tab, index) => (
                  <Tab label={tab.label} key={index} />
                ))}
              </Tabs>
            </Box>
            {tabs.map((tab, index) => (
              <TabPanel value={currentTabIndex} index={index} key={index}>
                {tab.component}
              </TabPanel>
            ))}
          </Card>
          <Dialog
            title={t('payoutContracts.showDetails.actions.pause.message')}
            onConfirm={handlePause}
            confirmLabel={t('payoutContracts.showDetails.actions.pause.label')}
            onClose={() => setShowPauseDialog(false)}
            open={showPauseDialog}
            showCancel
          >
            <Typography variant="body2">{t('payoutContracts.showDetails.actions.pause.text')}</Typography>
          </Dialog>
          <Dialog
            title={t('payoutContracts.showDetails.actions.unpause.message')}
            onConfirm={handleUnpause}
            confirmLabel={t('payoutContracts.showDetails.actions.unpause.label')}
            onClose={() => setShowUnpauseDialog(false)}
            open={showUnpauseDialog}
            showCancel
          >
            <Typography variant="body2">{t('payoutContracts.showDetails.actions.unpause.text')}</Typography>
          </Dialog>
          {showSetPayoutPerIndexDialog && (
            <SetPayoutPerIndex
              payoutContractId={id}
              payoutToken={payoutDetails.payoutToken}
              onClose={() => setShowSetPayoutPerIndexDialog(false)}
              onConfirm={() => setShowSetPayoutPerIndexDialog(false)}
            />
          )}
          {showSetEligibilityTokenDialog && (
            <SetEligibilityToken
              payoutContractId={id}
              eligibiltityTokenId={payoutDetails.tokenId}
              protocol={payoutDetails?.chain.protocol as ChainProtocol}
              onClose={() => setShowSetEligibilityTokenDialog(false)}
              onConfirm={() => setShowSetEligibilityTokenDialog(false)}
            />
          )}
          {showSetPayoutTokenDialog && (
            <SetPayoutToken
              payoutContractId={id}
              payoutTokenAddress={payoutDetails.payoutToken.address}
              protocol={payoutDetails?.chain.protocol as ChainProtocol}
              onClose={() => setShowSetPayoutTokenDialog(false)}
              onConfirm={() => setShowSetPayoutTokenDialog(false)}
            />
          )}
        </>
      )}
    </Container>
  )
}
